<!-- eslint-disable vue/no-useless-v-bind -->
<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import GroupConfiguration from './components/GroupConfiguration.vue';
import Linear from './linear/index.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LeadDataModal from 'dashboard/components/widgets/conversation/multi_leads/LeadDataModal.vue';

import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import MultiLeadsAPI from 'dashboard/api/integrations/multileads';
import ConversationCallButton from './ConversationCallButton.vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  isContactPanelOpen: {
    type: Boolean,
    default: false,
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  isInboxView: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['contactPanelToggle']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { updateUISettings, uiSettings } = useUISettings();

// State
const hideSidebarToggleButton = ref(false);
const showGroupConfig = ref(false);
const multiLeadsIntegration = ref(null);
const showLeadModal = ref(false);
const isLoadingLeadModal = ref(false);
const personData = ref(null);
const leadList = ref([]);
const tenantData = ref(null);
const multiLeadsPersonUrl = ref(null);
const multiLeadsLogo = '/dashboard/images/integrations/multileads.png';

// Computed
const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const appIntegrations = computed(
  () => store.getters['integrations/getAppIntegrations']
);
const isFeatureEnabledonAccount = computed(
  () => store.getters['accounts/isFeatureEnabledonAccount']
);

const chatMetadata = computed(() => props.chat.meta);

const isGroupConversation = computed(
  () => props.chat?.additional_attributes?.isGroup === true
);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) return true;
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const contactPanelToggleText = computed(() => {
  if (hideSidebarToggleButton.value) {
    return `${t('CONVERSATION.HEADER.OPEN')} ${t('CONVERSATION.HEADER.DETAILS')}`;
  }
  return `${
    props.isContactPanelOpen
      ? t('CONVERSATION.HEADER.CLOSE')
      : t('CONVERSATION.HEADER.OPEN')
  } ${t('CONVERSATION.HEADER.DETAILS')}`;
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const conversationProtocol = computed(
  () =>
    store.getters['conversationMetadata/getConversationMetadata'](
      currentChat.value.id
    )?.protocol || ''
);

const shouldShowProtocol = computed(() => {
  const isResponsive = props.showBackButton
    ? width.value > 900
    : width.value > 800;
  return conversationProtocol.value && isResponsive;
});

const isLinearIntegrationEnabled = computed(() =>
  appIntegrations.value.find(
    integration => integration.id === 'linear' && !!integration.hooks.length
  )
);

const isLinearFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.LINEAR)
);

// Watch
watch(currentChat, newChat => {
  const { isGroup } = newChat.additional_attributes;
  hideSidebarToggleButton.value = isGroup;
  if (isGroup) {
    updateUISettings({ is_contact_sidebar_open: false });
  }
});

// Methods
const toggleGroupConfig = () => {
  showGroupConfig.value = !showGroupConfig.value;
};

const handleToggle = () => {
  if (hideSidebarToggleButton.value) {
    toggleGroupConfig();
  } else {
    updateUISettings({
      is_contact_sidebar_open: !uiSettings.value.is_contact_sidebar_open,
    });
    emit('contactPanelToggle');
  }
};

// Keyboard shortcut Alt+O
const keyboardEvents = {
  'Alt+KeyO': {
    action: () => handleToggle(),
  },
};
useKeyboardEvents(keyboardEvents);

const checkMultiLeadsIntegration = async () => {
  try {
    const response = await MultiLeadsAPI.checkIntegration();
    multiLeadsIntegration.value = response.data.check_integration;
  } catch {
    multiLeadsIntegration.value = null;
  }
};

const handleLeadDataClick = async () => {
  if (!multiLeadsIntegration.value) return;
  isLoadingLeadModal.value = true;
  showLeadModal.value = true;
  personData.value = null;
  leadList.value = [];

  const token = multiLeadsIntegration.value.settings?.api_token;
  const contactId = props.chat.meta?.sender?.id;

  try {
    const personDataResponse = await MultiLeadsAPI.fetchPersonData(
      contactId,
      token
    );
    const person = personDataResponse.data.data?.[0] || null;
    personData.value = person;

    let leads = [];
    if (person?.id) {
      const leadDataResponse = await MultiLeadsAPI.fetchLeadData(
        person.id,
        token
      );
      leads = leadDataResponse.data.data || [];
    }
    leadList.value = leads;

    const tenantId = multiLeadsIntegration.value.settings?.tenant_id;
    if (tenantId) {
      const tenantResponse = await MultiLeadsAPI.fetchTenantData(
        tenantId,
        token
      );
      tenantData.value = tenantResponse.data;

      const domain = tenantData.value?.domains?.[0]?.domain;
      if (domain && personData.value?.id) {
        const baseUrl = domain.includes('localhost')
          ? `http://${domain}:8000`
          : `https://${domain}`;
        multiLeadsPersonUrl.value = `${baseUrl}/admin/contacts/persons/view/${personData.value.id}`;
      }
    }
  } catch {
    // toast não disponível diretamente em setup sem plugin, use o global
    window.$toast?.error('Erro ao buscar dados do lead.');
  } finally {
    isLoadingLeadModal.value = false;
  }
};

onMounted(() => {
  checkMultiLeadsIntegration();
});
const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="me-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div class="flex flex-col items-start min-w-0 ms-2 overflow-hidden">
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            <NextButton
              link
              slate
              class="text-truncate"
              @click.prevent="handleToggle"
            >
              <GroupConfiguration
                v-if="showGroupConfig"
                :show="showGroupConfig"
                :inbox="inbox"
                :current-contact="currentContact"
                :conversation="chat"
                @on-close="toggleGroupConfig"
              />
              <span
                class="text-base font-medium truncate leading-tight text-n-slate-12"
              >
                {{ currentContact.name }}
              </span>
            </NextButton>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
          </span>
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
          <NextButton
            link
            xs
            blue
            :label="contactPanelToggleText"
            @click="handleToggle"
          />
        </div>
      </div>
    </div>

    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
      :class="{ 'justify-end': isContactPanelOpen }"
    >
      <!-- Protocolo customizado -->
      <div
        v-if="shouldShowProtocol"
        class="flex m-0 p-0 w-[inherit] max-w-[8rem] justify-end"
      >
        <div class="ui-notification bg-yellow-100 dark:bg-slate-800">
          <div class="text-xxs text-slate-900 dark:text-yellow-100">
            {{ $t('CONVERSATION.HEADER.PROTOCOL_LABEL') }}:
          </div>
          <div
            class="protocol-number font-bold text-xs text-slate-900 dark:text-yellow-100"
          >
            {{ conversationProtocol }}
          </div>
        </div>
      </div>

      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />

      <!-- MultiLeads -->
      <woot-button
        v-if="multiLeadsIntegration && !isGroupConversation"
        v-tooltip="'MultiLeads CRM'"
        variant="clear"
        size="small"
        color-scheme="secondary"
        class="ml-2 flex items-center gap-1"
        @click="handleLeadDataClick"
      >
        <img :src="multiLeadsLogo" alt="MultiLeads" class="w-7 h-7" />
      </woot-button>

      <!-- Linear -->
      <Linear
        v-if="isLinearIntegrationEnabled && isLinearFeatureEnabled"
        :conversation-id="currentChat.id"
      />

      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>

    <!-- Modal MultiLeads -->
    <LeadDataModal
      v-if="showLeadModal && !isGroupConversation"
      :show="showLeadModal"
      :on-close="
        () => {
          showLeadModal = false;
        }
      "
      :is-loading="isLoadingLeadModal"
      :person-data="personData"
      :lead-list="leadList"
      :tenant-data="tenantData"
      :multi-leads-person-url="multiLeadsPersonUrl"
      :full-width="false"
      :contact-id="chat.meta.sender.id"
      :inbox-id="chat.inbox_id"
      :conversation-id="currentChat.id"
      size="medium"
      @lead-created="handleLeadDataClick"
    />
  </div>
</template>

<style lang="scss" scoped>
.conversation--header--actions {
  ::v-deep .inbox--name {
    @apply m-0;
  }
}
.ui-notification {
  display: flex;
  align-items: center;
  flex-direction: column;
  border-radius: var(--border-radius-small);
  box-shadow: var(--shadow-medium);
  padding: var(--space-small);

  .protocol-number {
    max-width: 7rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    direction: rtl;
  }
}
</style>
